import React from 'react';
import { useQuery } from 'react-query';
import { Users, MapPin, DollarSign, ShoppingCart, TrendingUp, Package } from 'lucide-react';
import StatCard from '../components/UI/StatCard';
import LineChart from '../components/UI/LineChart';
import BarChart from '../components/UI/BarChart';
import PieChart from '../components/UI/PieChart';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Dashboard: React.FC = () => {
  const { currentFlavourId } = useAuth();
  
  // Fetch dashboard stats - works even without flavourId (shows all data for super admin)
  const { data: stats, isLoading, error } = useQuery(
    ['dashboardStats', currentFlavourId],
    () => apiService.getDashboardStats(),
    {
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load dashboard data</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Welcome to Trippy Admin - Overview of your tour applications</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Users"
          value={stats?.totalUsers || 0}
          icon={Users}
          color="blue"
          change={`${stats?.userChangePercent >= 0 ? '+' : ''}${stats?.userChangePercent?.toFixed(1) || '0.0'}%`}
          changeType={stats?.userChangePercent >= 0 ? 'positive' : 'negative'}
        />
        <StatCard
          title="Total Tours"
          value={stats?.totalTours || 0}
          icon={MapPin}
          color="green"
          change={`${stats?.tourChangePercent >= 0 ? '+' : ''}${stats?.tourChangePercent?.toFixed(1) || '0.0'}%`}
          changeType={stats?.tourChangePercent >= 0 ? 'positive' : 'negative'}
        />
        <StatCard
          title="Total Revenue"
          value={`$${stats?.totalRevenue?.toLocaleString() || 0}`}
          icon={DollarSign}
          color="purple"
          change={`${stats?.revenueChangePercent >= 0 ? '+' : ''}${stats?.revenueChangePercent?.toFixed(1) || '0.0'}%`}
          changeType={stats?.revenueChangePercent >= 0 ? 'positive' : 'negative'}
        />
        <StatCard
          title="Total Bookings"
          value={stats?.totalBookings || 0}
          icon={ShoppingCart}
          color="orange"
          change={`${stats?.bookingChangePercent >= 0 ? '+' : ''}${stats?.bookingChangePercent?.toFixed(1) || '0.0'}%`}
          changeType={stats?.bookingChangePercent >= 0 ? 'positive' : 'negative'}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <LineChart
          data={stats?.revenueData || []}
          title="Revenue Trend"
          color="#3B82F6"
        />
        <BarChart
          data={stats?.toursVsUsers || []}
          title="Tours vs Users"
          color="#10B981"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PieChart
          data={stats?.tourStatusDistribution || []}
          title="Tour Status Distribution"
        />
        
        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-4">
            {stats?.recentActivity && stats.recentActivity.length > 0 ? (
              stats.recentActivity.map((activity: any, index: number) => {
                const colorMap: { [key: string]: string } = {
                  green: 'bg-green-500',
                  blue: 'bg-blue-500',
                  purple: 'bg-purple-500',
                  orange: 'bg-orange-500',
                };
                const bgColor = colorMap[activity.color] || 'bg-gray-500';
                
                return (
                  <div key={index} className="flex items-center space-x-3">
                    <div className={`w-2 h-2 ${bgColor} rounded-full`}></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {activity.message}
                        {activity.entityName && (
                          <span className="text-gray-600"> - {activity.entityName}</span>
                        )}
                        {activity.amount && (
                          <span className="text-gray-600"> (${parseFloat(activity.amount).toFixed(2)})</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500">{activity.timeAgo || 'Just now'}</p>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">No recent activity</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 